import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { motion } from 'framer-motion';
import {
  Box,
  TextField,
  Button,
  Card,
  Typography,
  InputLabel,
  MenuItem,
  Select,
  FormControl,
  useMediaQuery,
  useTheme,
  Paper,
  // Grid,
} from '@mui/material';
// import { VideoCameraBack, CameraAlt } from '@mui/icons-material';

import logo from '../../images/logo.png';
// import { uploadMedia } from '../../utils/MediaUploader';
import { ReportContext } from '../ReportContext';
import { AuthContext } from '../../auth/AuthContext';
import { showSwalNotification } from '../../utils/SwalResponse';
import MyWebsite from '../../utils/MyWebsite';
// import MediaPreview from '../../utils/MediaPreview';
import MediaContainer from './util/MediaContainer';

const forestGreen = '#228B22';
const LOCAL_STORAGE_KEY = 'reportFormData';

const ReportIssue = () => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const { authToken } = useContext(AuthContext);
  const { loading, addNewReport, setLoading } = useContext(ReportContext);
  const navigate = useNavigate();
  const didRun = useRef(false);
  // const photoInputRef = useRef(null);
  // const videoInputRef = useRef(null);

  const [formData, setFormData] = useState({
    description: '',
    category: '',
    website: '',
    media: null,
    media_url: null,
    latitude: null,
    longitude: null,
  });
//   const [isMobile, setIsMobile] = useState(false);

// useEffect(() => {
//   const userAgent = navigator.userAgent || navigator.vendor || window.opera;
//   const mobileCheck = /android|ipad|iphone|ipod/i.test(userAgent);
//   setIsMobile(mobileCheck);
// }, []);

  useEffect(() => {
    // Redirect to external link
    window.location.href = "https://mazingiraguard.org/report"; 
  }, []);

  // Load from localStorage on mount
  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY));
    if (saved) {
      setFormData((prev) => ({
        ...prev,
        ...saved,
      }));
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...formData, [name]: value };
    setFormData(updated);
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updated));
  };

  // const handleFileChange = async (e) => {
  //   const file = e.target.files[0];
  //   if (!file) return;

  //   if (!file?.type?.startsWith('image/') && !file?.type?.startsWith('video/')) {
  //     Swal.fire('Invalid File', 'Only images and videos are allowed.', 'warning');
  //     return;
  //   }

  //   const updated = { ...formData, media: file };
  //   setFormData(updated);
  //   localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updated));

  //   const uploaded = await uploadMedia(file);
  //   if (uploaded?.url) {
  //     const updatedWithUrl = { ...updated, media_url: uploaded.url };
  //     setFormData(updatedWithUrl);
  //     localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(updatedWithUrl));
  //   } else {
  //     Swal.fire('Upload Failed', 'Could not upload media. Try again.', 'error');
  //     const fallback = { ...formData, media: null };
  //     setFormData(fallback);
  //     localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(fallback));
  //   }
  // };

  const getCurrentCoordinates = () =>
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) reject(new Error('Geolocation not supported.'));
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;
          resolve({ latitude, longitude });
        },
        (err) => {
          console.error('Geolocation error:', err);
          reject(err);
        }
      );
    });

  useEffect(() => {
    if (didRun.current) return;
    didRun.current = true;
    const tryResubmit = async () => {
      const localData = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY));
      if (
        !!authToken &&
        localData?.description &&
        localData?.category &&
        localData?.latitude &&
        localData?.longitude &&
        localData?.media_url
      ) {
        setLoading(true);
        const response = await addNewReport(localData);
        showSwalNotification(response);
        if (response?.status === 'success') {
          localStorage.removeItem(LOCAL_STORAGE_KEY);
          navigate(`/reports/${response?.data?.report_id}/details`);
        }
        setLoading(false);
      }
    };
    tryResubmit();
  }, [authToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.website) {
      setLoading(false);
      Swal.fire('Invalid Input', 'Bot detected.', 'warning');
      return;
    }

    try {
      setLoading(true);
      const position = await getCurrentCoordinates();
      const completeFormData = {
        ...formData,
        latitude: position.latitude,
        longitude: position.longitude,
      };

      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(completeFormData));

      if (!authToken) {
        Swal.fire({
          icon: 'info',
          title: 'Login Required',
          text: 'Please log in to submit your report.',
        });
        return navigate('/login?rep=submit');
      }

      const response = await addNewReport(completeFormData);

      if (response?.msg) {
        Swal.fire({
          icon: 'info',
          title: 'Session Expired',
          text: 'Please log in again to continue.',
        });
        return navigate('/login?rep=submit');
      }

      showSwalNotification(response);

      if (response?.status === 'success') {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        navigate(`/reports/${response?.data?.report_id}/details`);
      }
    } catch (error) {
      console.error('Submission error:', error);
      Swal.fire('Error', 'An error occurred while submitting the report.', 'error');
    } finally {
      setLoading(false);
    }
  };

   if (isDesktop) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="d-flex justify-content-center align-items-center vh-100 p-4 "
      >

        <Paper elevation={6} sx={{ p: 4, maxWidth: 600, borderRadius: 4, textAlign: 'center' }}>
          <Box mb={2}>
            <img src={logo} alt="App Logo" style={{ maxWidth: 160 }} />
          </Box>
          <Typography variant="h5" fontWeight={600} color="primary" gutterBottom>
            Environmental Reporting Form
          </Typography>
          <Typography variant="body1" gutterBottom>
            This form is only available on mobile devices for live geolocation tracking.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please visit this page using your smartphone or tablet. Live location is required to report accurate issue locations.
          </Typography>
          <Button
          variant="outlined"
          onClick={() => navigate(-1)}
          size="small"
          sx={{
            mb: 1,
            textTransform: 'none',
            fontWeight: 500,
            borderColor: theme.palette.success.main,
            color: theme.palette.success.main,
            '&:hover': {
              borderColor: theme.palette.warning.dark,
              backgroundColor: 'rgba(34, 139, 38, 0.5)',
            },
          }}
        >
          ← Back
        </Button>
        </Paper>
        
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="d-flex justify-content-center align-items-center"
      style={{ minHeight: '100vh', background: 'none' }}
    >
      <motion.div
        initial={{ y: 30, scale: 0.98 }}
        animate={{ y: 0, scale: 1 }}
        transition={{ duration: 0.4 }}
      >
        <Card
          sx={{
            p: 4,
            width: '90vw',
            maxWidth: 500,
            borderRadius: 4,
            border: `1px solid ${forestGreen}`,
            backgroundColor: 'rgba(255,255,255,0.9)',
            position: 'relative',
          }}
          elevation={6}
        >
          <Button
            onClick={() => window.history.back()}
            size="small"
            sx={{ position: 'absolute', top: 8, right: 8 }}
          >
            ❌
          </Button>

          <Box textAlign="center" mb={2}>
            <img src={logo} alt="App Logo" style={{ width: 150 }} />
          </Box>

          <Typography variant="h5" textAlign="center" color={forestGreen} fontWeight={600} mb={2}>
            Report an Issue
          </Typography>

          <Box component="form" onSubmit={handleSubmit}>

            <MyWebsite onChange={handleChange} />
            

            {/* Media Upload */}
              {/* <Box mt={2}>
                <Typography fontWeight={500} mb={1}>
                  Upload Media (Image or Video)
                </Typography>
                <Grid container spacing={2} alignItems="center" gap={2} justifyContent="center" flexWrap={"wrap"}>

                Take Photo Button
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => photoInputRef.current.click()}
                  sx={{ mr: 2 }}
                  startIcon={<CameraAlt />}
                  size="small"
                >
                  Photo
                </Button>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  ref={photoInputRef}
                  onChange={handleFileChange}
                  required={!formData.media_url}
                  style={{ display: 'none' }}
                />
                <span>OR</span>
                Record Video Button
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => videoInputRef.current.click()}
                  startIcon={<VideoCameraBack />}
                  size="small"
                >
                   Video
                </Button>
                <input
                  type="file"
                  accept="video/*"
                  capture="environment"
                  ref={videoInputRef}
                  onChange={handleFileChange}
                  required={!formData.media_url}
                  style={{ display: 'none' }}
                />
                </Grid>

                Show selected media
                {formData.media && (
                  <Typography variant="body2" color="green" mt={1}>
                    Selected: {formData.media.name}
                  </Typography>
                )}

           {formData?.media_url && (<MediaPreview src={formData?.media_url} />)}
              </Box> */}
              <MediaContainer formData={formData} setFormData={setFormData}/>
               {/* Description */}
            <TextField
              label="Description (optional)"
              name="description"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={4}
              fullWidth
              
              margin="normal"
              sx={{ '& .MuiOutlinedInput-root': { borderColor: forestGreen } }}
            />

              {/* Category */}
            <FormControl fullWidth margin="normal">
              <InputLabel>Category</InputLabel>
              <Select
                label="Category"
                name="category"
                value={formData.category}
                onChange={handleChange}
                required
              >
                {[
                  'Illegal Dumping',
                  'Air Pollution',
                  'Water Pollution',
                  'Soil Pollution',
                  'Littering',
                  'Hazardous Waste Disposal',
                  'Deforestation',
                  'Noise Pollution',
                  'Plastic Pollution',
                  "I don't know ",
                  "Other",
                ].map((cat, i) => (
                  <MenuItem key={i} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>



            {/* Submit Button */}
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{
                  mt: 3,
                  backgroundColor: forestGreen,
                  fontWeight: 600,
                  borderRadius: 2,
                  opacity: loading ? 0.7 : 1,
                }}
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Submit Report'}
              </Button>
            </motion.div>

            {/* Terms and Privacy Note */}
            <Typography
              variant="caption"
              align="center"
              display="block"
              sx={{ mt: 1.5, color: '#555' }}
            >
              By submitting, you agree to our{' '}
              <Link to="/terms" style={{ color: forestGreen, textDecoration: 'underline' }}>
                Terms
              </Link>{' '}
              and{' '}
              <Link to="/privacy" style={{ color: forestGreen, textDecoration: 'underline' }}>
                Privacy Policy
              </Link>.
            </Typography>
          </Box>
        </Card>
      </motion.div>
    </motion.div>
  );
};

export default ReportIssue;